import {
  computed,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'
import { useQuery } from '@vue/apollo-composable'

import type { InfringingFileDetailsQuery } from '@/shared/graphql/router/graphql'

import { INFRINGING_FILE_DETAILS_QUERY } from './operations'

export type InfringingFileDetails =
  InfringingFileDetailsQuery['infringement']['infringingFile']

const POLL_MS = 3_000
const WAIT_MS = 60_000
const LIVE_CAPTURE_TYPES = ['Visual capture', 'Audio recording']

export const useInfringingFileDetails = (
  matchId: MaybeRefOrGetter<string | null | undefined>
) => {
  const id = computed(() => toValue(matchId) ?? '')
  const timedOut = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null

  watch(
    id,
    (value) => {
      timedOut.value = false
      if (timer) clearTimeout(timer)
      timer = value ? setTimeout(() => (timedOut.value = true), WAIT_MS) : null
    },
    { immediate: true }
  )

  const waitingForIndex = ref(false)

  const { result, error } = useQuery(
    INFRINGING_FILE_DETAILS_QUERY,
    () => ({ id: id.value }),
    () => ({
      enabled: !!id.value,
      clientId: 'router',
      fetchPolicy: 'cache-and-network',
      pollInterval: waitingForIndex.value ? POLL_MS : 0,
    })
  )

  const file = computed<InfringingFileDetails | null>(
    () => result.value?.infringement.infringingFile ?? null
  )

  const waiting = computed(() => {
    if (!id.value || timedOut.value) return false
    if (error.value || !file.value) return true
    if (file.value.copyrightId) return false
    const metadata = file.value.metadata
    if (!metadata) return true
    return (
      LIVE_CAPTURE_TYPES.includes(metadata.captureType ?? '') &&
      (metadata.recordedAt == null || metadata.durationSeconds == null)
    )
  })

  watch(waiting, (value) => (waitingForIndex.value = value), {
    immediate: true,
  })

  onBeforeUnmount(() => {
    if (timer) clearTimeout(timer)
  })

  return { file, waiting }
}
